// The keys focused controls handle themselves, read by each control through
// `controlCommand` and listed in the key guide from these same tables.

import type { ControlKeymap } from './control';

/** A tab list's keys (WAI-ARIA tabs, automatic activation): the Overview's brief tabs and the Publish summary's Write / Preview. */
export type TabListCommand = 'prev' | 'next' | 'first' | 'last';

export const tabListKeys: ControlKeymap<TabListCommand> = {
  id: 'tablist',
  title: 'Tab lists',
  rows: [
    { keys: [['ArrowLeft', 'prev'], ['ArrowRight', 'next']], does: 'On a tab list (the brief’s tabs, the summary’s Write / Preview): previous / next tab' },
    { keys: [['Home', 'first'], ['End', 'last']], does: 'On a tab list: first / last tab' },
  ],
};

/** A commit point of the range timeline. */
export type TimelinePointCommand =
  | { kind: 'focus'; to: 'prev' | 'next' | 'first' | 'last' }
  /** Open the Set as From / Set as To menu. */
  | { kind: 'menu' }
  | { kind: 'set'; handle: 'from' | 'to' };

export const timelinePointKeys: ControlKeymap<TimelinePointCommand> = {
  id: 'timeline-point',
  title: 'Range timeline commits',
  rows: [
    {
      keys: [
        ['ArrowLeft', { kind: 'focus', to: 'prev' }],
        ['ArrowRight', { kind: 'focus', to: 'next' }],
      ],
      does: 'Move between commits',
    },
    {
      keys: [
        ['Home', { kind: 'focus', to: 'first' }],
        ['End', { kind: 'focus', to: 'last' }],
      ],
      does: 'First / last commit',
    },
    {
      keys: [
        ['Enter', { kind: 'menu' }],
        ['Space', { kind: 'menu' }],
      ],
      does: 'Choose Set as From / Set as To for the focused commit',
    },
    {
      keys: [
        ['Shift+Enter', { kind: 'set', handle: 'to' }],
        ['Alt+Enter', { kind: 'set', handle: 'from' }],
        ['Shift+Space', { kind: 'set', handle: 'to' }],
        ['Alt+Space', { kind: 'set', handle: 'from' }],
      ],
      does: 'Set To / From to the focused commit',
    },
  ],
};

/** The range timeline's A / B handles: a move is the key itself, read by `keyMove` (logic/range.ts). */
export type TimelineHandleCommand = { kind: 'move'; key: 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown' | 'Home' | 'End' } | { kind: 'apply' };

export const timelineHandleKeys: ControlKeymap<TimelineHandleCommand> = {
  id: 'timeline-handle',
  title: 'Range timeline handles',
  rows: [
    {
      keys: [
        ['ArrowLeft', { kind: 'move', key: 'ArrowLeft' }],
        ['ArrowRight', { kind: 'move', key: 'ArrowRight' }],
      ],
      does: 'Move the focused A / B handle one commit',
    },
    {
      keys: [
        ['ArrowUp', { kind: 'move', key: 'ArrowUp' }],
        ['ArrowDown', { kind: 'move', key: 'ArrowDown' }],
      ],
      does: 'Move the focused handle to the replaced commits / back to the main line',
    },
    {
      keys: [
        ['Home', { kind: 'move', key: 'Home' }],
        ['End', { kind: 'move', key: 'End' }],
      ],
      does: 'Move the focused handle to the first / last commit it can take',
    },
    {
      keys: [
        ['Enter', { kind: 'apply' }],
        ['Space', { kind: 'apply' }],
      ],
      does: 'Show the moved range now',
    },
  ],
};

/** The Set as From / Set as To menu of a commit point. */
export type TimelineMenuCommand = 'prev' | 'next' | 'close';

export const timelineMenuKeys: ControlKeymap<TimelineMenuCommand> = {
  id: 'timeline-menu',
  title: 'Range timeline menu',
  rows: [
    {
      keys: [
        ['ArrowLeft', 'prev'],
        ['ArrowRight', 'next'],
        ['ArrowUp', 'prev'],
        ['ArrowDown', 'next'],
      ],
      does: 'In the Set as menu: previous / next choice',
    },
    { keys: [['Escape', 'close']], does: 'Close the Set as menu, back to its commit' },
  ],
};

/** A stack's map on the dashboard (a toolbar of its pull requests). */
export type StackMapCommand = 'prev' | 'next' | 'first' | 'last';

export const stackMapKeys: ControlKeymap<StackMapCommand> = {
  id: 'stack-map',
  title: 'Stack map',
  rows: [
    {
      keys: [
        ['ArrowLeft', 'prev'],
        ['ArrowRight', 'next'],
        ['ArrowUp', 'prev'],
        ['ArrowDown', 'next'],
      ],
      does: 'On a stack’s map: previous / next pull request in the stack',
    },
    { keys: [['Home', 'first'], ['End', 'last']], does: 'On a stack’s map: first / last pull request' },
  ],
};

/** The dashboard's overview: its runs' counts (and a failing run's filter), across the sections' cards. */
export type OverviewCommand = 'prev' | 'next' | 'first' | 'last';

export const overviewKeys: ControlKeymap<OverviewCommand> = {
  id: 'dash-overview',
  title: 'Dashboard overview',
  rows: [
    {
      keys: [
        ['ArrowLeft', 'prev'],
        ['ArrowRight', 'next'],
        ['ArrowUp', 'prev'],
        ['ArrowDown', 'next'],
      ],
      does: 'On the overview: previous / next run of pull requests (Enter goes to its next row)',
    },
    { keys: [['Home', 'first'], ['End', 'last']], does: 'On the overview: first / last run' },
  ],
};

/** The Find file picker's list of matching files. */
export type FindFileListCommand = 'prev' | 'next' | 'first' | 'last' | 'open' | 'close';

export const findFileListKeys: ControlKeymap<FindFileListCommand> = {
  id: 'find-file-list',
  title: 'Find file',
  rows: [
    { keys: [['ArrowUp', 'prev'], ['ArrowDown', 'next']], does: 'In the Find file picker: previous / next matching file' },
    { keys: [['Home', 'first'], ['End', 'last']], does: 'In the Find file picker: first / last matching file' },
    { keys: [['Enter', 'open']], does: 'Open the matching file in Files' },
    { keys: [['Escape', 'close']], does: 'Close the Find file picker' },
  ],
};

/** The pull request header's stack menu: the stack's pull requests, base first. */
export type StackMenuCommand = 'prev' | 'next' | 'first' | 'last' | 'open' | 'close';

export const stackMenuKeys: ControlKeymap<StackMenuCommand> = {
  id: 'stack-menu',
  title: 'Stack menu',
  rows: [
    {
      keys: [
        ['ArrowUp', 'prev'],
        ['ArrowDown', 'next'],
        ['k', 'prev'],
        ['j', 'next'],
      ],
      does: 'In the stack menu: the pull request below (towards the base) / above',
    },
    { keys: [['Home', 'first'], ['End', 'last']], does: 'In the stack menu: the base / the tip of the stack' },
    { keys: [['Enter', 'open']], does: 'Open that pull request on the view you are on (with Ctrl or ⌘, in a new tab)' },
    { keys: [['Escape', 'close']], does: 'Close the stack menu, back to its chip' },
  ],
};

/** The jump bar's field: its list of results, each with its actions (components/JumpBar.svelte). */
export type JumpBarCommand = 'prev-row' | 'next-row' | 'prev-action' | 'next-action' | 'take' | 'close';

export const jumpBarKeys: ControlKeymap<JumpBarCommand> = {
  id: 'jump-bar',
  title: 'Jump bar',
  rows: [
    { keys: [['ArrowUp', 'prev-row'], ['ArrowDown', 'next-row']], does: 'In the jump bar: previous / next result' },
    { keys: [['Tab', 'next-action'], ['Shift+Tab', 'prev-action']], does: 'Next / previous action: a result’s pull request, Review against the default branch, History, Pin' },
    { keys: [['Enter', 'take']], does: 'Take the action shown: a result’s first, unless Tab moved on' },
    { keys: [['Escape', 'close']], does: 'Close the jump bar' },
  ],
};

/** A header picker's field (components/RefPicker.svelte): its list of branches, each one choice. */
export type HeaderPickerCommand = 'prev' | 'next' | 'take' | 'close';

export const headerPickerKeys: ControlKeymap<HeaderPickerCommand> = {
  id: 'header-picker',
  title: 'Base, head and ref pickers',
  rows: [
    { keys: [['ArrowUp', 'prev'], ['ArrowDown', 'next']], does: 'In a header’s picker: previous / next branch' },
    { keys: [['Enter', 'take']], does: 'Go to the compare (or history) with the branch shown, or the name as typed' },
    { keys: [['Escape', 'close']], does: 'Close the picker, back to the name it changes' },
  ],
};
